import { useCallback, useState } from 'react';
import type { ConfirmationModalConfig, ModalAction } from '../components/ConfirmationModal/ConfirmationModal';
import type { MarketListItem } from '../components/SelectionBar/SelectionBar';

export interface MarketLinkAction {
  actionType: 'connect' | 'disconnect';
  marketCode: number;
  marketShortName: string;
  mainMarketCode: number;
}

interface UseMarketModalOptions {
  /** Callback when user confirms the market link action (YES) */
  onConfirmAction: (action: MarketLinkAction) => void;
}

/**
 * Hook managing the confirmation modal for market connect/disconnect.
 * - Connect: "Do you want to replace current article plan for {region} with {mainMarket} article plan?"
 * - Disconnect: "Do you want to disconnect {region} from {mainMarket}?"
 */
export function useMarketModal({ onConfirmAction }: UseMarketModalOptions) {
  const [pendingAction, setPendingAction] = useState<MarketLinkAction | null>(null);
  const [mainMarketName, setMainMarketName] = useState('');

  // ── Open the market link confirmation modal ───────────────────────
  const openMarketLinkModal = useCallback(
    (market: MarketListItem, mainMarket: string, mainMarketCode: number, actionType: 'connect' | 'disconnect') => {
      setPendingAction({
        actionType,
        marketCode: Number(market.value),
        marketShortName: market.label,
        mainMarketCode,
      });
      setMainMarketName(mainMarket);
    },
    [],
  );

  // ── Close/cancel ──────────────────────────────────────────────────
  const closeModal = useCallback(() => {
    setPendingAction(null);
  }, []);

  // ── Build modal config ────────────────────────────────────────────
  const getMarketModalConfig = useCallback((): ConfirmationModalConfig => {
    if (!pendingAction) {
      return { isOpen: false, header: '', message: '', actions: [], onClose: () => { /* no-op */ } };
    }

    const isConnect = pendingAction.actionType === 'connect';

    const actions: ModalAction[] = [
      {
        label: 'YES',
        mode: 'primary',
        onClick: () => {
          onConfirmAction(pendingAction);
          closeModal();
        },
      },
      {
        label: 'NO, keep the article plan',
        mode: 'information',
        onClick: () => {
          closeModal();
        },
      },
    ];

    const header = isConnect ? 'Replace plan' : 'Disconnect market';
    const message = isConnect
      ? `Do you want to replace current article plan for ${pendingAction.marketShortName} with ${mainMarketName} article plan?`
      : `Do you want to disconnect ${pendingAction.marketShortName} from ${mainMarketName}?`;

    return {
      isOpen: true,
      header,
      message,
      actions,
      onClose: closeModal,
    };
  }, [pendingAction, mainMarketName, onConfirmAction, closeModal]);

  return {
    marketModalConfig: getMarketModalConfig(),
    openMarketLinkModal,
  };
}
